import type { CompRef } from '@wix/document-services-types';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';

import { ManifestStructureService } from '../sharedServices';
import { VirtualManifestAPI } from '../api';
import { ManifestPlatformService } from './ManifestPlatformService';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class HierarchyPlatformService extends AbstractComponentsPlatformService {
  readonly #manifestStructureService: ManifestStructureService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.#manifestStructureService = new ManifestStructureService(
      this.editorPointerAPI,
    );
  }

  /**
   * this should work only for the single pointer
   * there shouldn't be the case when we check isBoxLike for multiple components
   * */
  isBoxLike(editorPointer: EditorPointer<CompRef>) {
    if (!editorPointer) {
      return false;
    }

    const element = this.virtualElementProvider.buildVirtualElement({
      editorPointers: [editorPointer],
    });

    if (!element) {
      return false;
    }

    const api = element.getAPI();
    return ManifestPlatformService.isBoxLike(
      api.resolver.getLayoutDefinitions(),
      api.resolver.getDataDefinitions(),
    );
  }

  /**
   * NOTE method will return children elements filtered by display filters
   * this should work only for the single pointer
   * there shouldn't be the case when we check getChildrenEditorPointers for multiple components
   */
  getChildrenEditorPointers(editorPointer: EditorPointer<CompRef>) {
    const children: EditorPointer<CompRef>[] = [];

    const collect = (api: VirtualManifestAPI) => {
      api.getChildren().forEach((child) => {
        const childEditorPointer = child.getEditorPointer();
        if (childEditorPointer) {
          children.push(childEditorPointer);
          collect(child);
        }
      });
    };

    collect(
      this.virtualElementProvider
        .buildVirtualElement({
          editorPointers: [editorPointer],
        })
        .getAPI(),
    );

    return children;
  }

  getChildren(
    editorPointer: EditorPointer<CompRef>,
    options?: {
      isRecursive?: boolean;
      /**
       * @deprecated Use `filter.displayFilters` and `filter.stageVisibility` instead.
       * Use the new API for granular control.
       */
      filterHiddenElements?: boolean;
      filter?: {
        displayFilters?: boolean;
        stageVisibility?: boolean;
      };
    },
  ) {
    const { isRecursive = false, filterHiddenElements, filter } = options ?? {};

    // displayFilters were always applied in the original implementation
    const shouldFilterByDisplayFilters = filter?.displayFilters ?? true;
    const shouldFilterByStageVisibility =
      filter?.stageVisibility ?? filterHiddenElements ?? false;

    const root = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    const isChildVisible = (child: VirtualManifestAPI): boolean => {
      if (shouldFilterByDisplayFilters) {
        if (!child.resolver.isElementEditable()) {
          return false;
        }
      }

      if (shouldFilterByStageVisibility) {
        /**
         * TODO: as soon as API is ready
         * replace with
         * child.query.isElementVisibleOnStage()
         */
        const { cssProperties, cssCustomProperties } = child.query.getStyles();
        const displayValue =
          cssProperties?.display || cssCustomProperties?.display;

        if (
          displayValue &&
          (displayValue === 'none' || displayValue === 'MIX')
        ) {
          return false;
        }
      }

      return true;
    };

    const getNodeChildren = (
      node: VirtualManifestAPI,
    ): EditorPointer<CompRef>[] => {
      return node
        .getChildren()
        .filter(isChildVisible)
        .flatMap((child) => {
          const childPointer = child.getEditorPointer();
          const children = isRecursive ? getNodeChildren(child) : [];
          return [childPointer, ...children];
        });
    };

    return getNodeChildren(root);
  }

  getAncestors(editorPointer: EditorPointer<CompRef>) {
    if (!editorPointer) {
      return [];
    }
    return this.#manifestStructureService.getAncestors(editorPointer);
  }
}
